import { useEffect, useState } from "react";
import { useAdmin, adminAxios } from "./AdminContext";
import { SERVICES } from "@/data/site";
import { MessageSquareQuote, Check, Trash2, Filter, Pencil, Save, X } from "lucide-react";

const FILTERS = [
  { value: "pending", label: "Bekleyen" },
  { value: "approved", label: "Onaylı" },
  { value: "rejected", label: "Reddedilen" },
  { value: "all", label: "Tümü" },
];
const SERVICE_OPTIONS = SERVICES.map(s => s.title);

export default function TestimonialsAdmin() {
  const { token } = useAdmin();
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", company: "", service: "", comment: "" });
  const [editError, setEditError] = useState("");

  const reload = async () => {
    const path = filter === "all" ? "/admin/testimonials" : `/admin/testimonials?status=${filter}`;
    const r = await adminAxios(token).get(path);
    setItems(r.data.items || []);
  };
  useEffect(() => { reload(); }, [filter, token]); // eslint-disable-line

  const approve = async (id) => {
    setBusyId(id);
    try { await adminAxios(token).post(`/admin/testimonials/${id}/action`, { action: "approve" }); await reload(); }
    finally { setBusyId(null); }
  };
  const remove = async (id) => {
    if (!window.confirm("Bu yorum kalıcı olarak silinsin mi?")) return;
    setBusyId(id);
    try { await adminAxios(token).delete(`/admin/testimonials/${id}`); await reload(); }
    finally { setBusyId(null); }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setEditError("");
    setEditForm({
      name: t.name || "",
      company: t.company || "",
      service: t.service || "",
      comment: t.comment || "",
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditError(""); };
  const saveEdit = async (id) => {
    if (!editForm.name.trim() || editForm.comment.trim().length < 10) {
      setEditError("İsim zorunlu ve yorum en az 10 karakter olmalı.");
      return;
    }
    setBusyId(id);
    try {
      await adminAxios(token).put(`/admin/testimonials/${id}`, {
        name: editForm.name,
        company: editForm.company || null,
        service: editForm.service || null,
        comment: editForm.comment,
      });
      setEditingId(null);
      await reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setEditError(typeof detail === "string" ? detail : "Güncelleme başarısız.");
    } finally { setBusyId(null); }
  };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("tr-TR"); } catch { return iso; } };

  return (
    <div className="space-y-6" data-testid="admin-testimonials">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3"><MessageSquareQuote size={26} /> Yorumlar</h1>
        <p className="text-white/50 mt-2">Müşteri yorumlarını onaylayın, düzenleyin, reddedin veya silin.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="admin-testimonials-filter">
        <Filter size={14} className="text-white/40" />
        {FILTERS.map(f => (
          <button key={f.value} onClick={() => { setFilter(f.value); setEditingId(null); }}
            data-testid={`admin-filter-${f.value}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${filter === f.value ? "bg-[#FF3B30] text-white border-[#FF3B30]" : "bg-white/5 text-white/70 border-white/15 hover:border-white/40"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="admin-testimonials-list">
        {items.length === 0 ? (
          <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-10 text-center text-white/50 text-sm">Bu kategoride yorum yok.</div>
        ) : items.map(t => (
          <article key={t.id} className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 md:p-6" data-testid={`admin-testimonial-${t.id}`}>
            {editingId === t.id ? (
              <div className="space-y-4" data-testid={`admin-edit-${t.id}`}>
                <div className="grid md:grid-cols-2 gap-3">
                  <EditField label="İsim *">
                    <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="input-t" />
                  </EditField>
                  <EditField label="Firma">
                    <input value={editForm.company} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} className="input-t" />
                  </EditField>
                </div>
                <EditField label="Hizmet">
                  <select value={editForm.service} onChange={(e) => setEditForm({ ...editForm, service: e.target.value })} className="input-t">
                    <option value="" style={{ background: "#0A0A0A", color: "#fff" }}>Seçilmedi</option>
                    {SERVICE_OPTIONS.map(s => <option key={s} value={s} style={{ background: "#0A0A0A", color: "#fff" }}>{s}</option>)}
                  </select>
                </EditField>
                <EditField label="Yorum *">
                  <textarea rows={5} minLength={10} maxLength={1000} value={editForm.comment} onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })} className="input-t resize-y" />
                </EditField>
                {editError && <div className="text-xs text-[#FF6B63]" data-testid={`admin-edit-error-${t.id}`}>{editError}</div>}
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(t.id)} disabled={busyId === t.id}
                    data-testid={`admin-save-${t.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#FF3B30] text-white px-4 py-2 text-xs font-bold hover:bg-white hover:text-[#0A0A0A] transition disabled:opacity-50">
                    <Save size={14} /> Kaydet
                  </button>
                  <button onClick={cancelEdit} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-white px-4 py-2 text-xs font-bold hover:bg-white/20">
                    <X size={14} /> İptal
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-semibold">{t.name} {t.company && <span className="text-white/50">— {t.company}</span>}</div>
                    <div className="text-[11px] tracking-[0.2em] uppercase mt-1 flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full ${
                        t.status === "approved" ? "bg-[#34C759]/15 text-[#4ADE80]" :
                        t.status === "rejected" ? "bg-white/10 text-white/50" : "bg-[#FF3B30]/15 text-[#FF6B63]"
                      }`}>{t.status === "pending" ? "Beklemede" : t.status === "approved" ? "Onaylı" : "Reddedildi"}</span>
                      {t.service && <span className="text-white/50">{t.service}</span>}
                      <span className="text-white/40">{fmtDate(t.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(t)} disabled={busyId === t.id}
                      data-testid={`admin-edit-btn-${t.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white/5 text-white/80 border border-white/15 px-3.5 py-1.5 text-xs font-bold hover:bg-white/10 disabled:opacity-50">
                      <Pencil size={14} /> Düzenle
                    </button>
                    {t.status !== "approved" && (
                      <button onClick={() => approve(t.id)} disabled={busyId === t.id}
                        data-testid={`admin-approve-${t.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#34C759] text-[#0A0A0A] px-3.5 py-1.5 text-xs font-bold hover:opacity-90 disabled:opacity-50">
                        <Check size={14} /> Onayla
                      </button>
                    )}
                    <button onClick={() => remove(t.id)} disabled={busyId === t.id}
                      data-testid={`admin-delete-${t.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white/5 text-[#FF6B63] border border-[#FF3B30]/30 px-3.5 py-1.5 text-xs font-bold hover:bg-[#FF3B30]/10 disabled:opacity-50">
                      <Trash2 size={14} /> Sil
                    </button>
                  </div>
                </div>
                <p className="mt-4 text-white/80 leading-relaxed whitespace-pre-line">{t.comment}</p>
              </>
            )}
          </article>
        ))}
      </div>

      <style>{`.input-t{width:100%;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.12);border-radius:10px;padding:10px 12px;color:#fff;font-size:14px;outline:none}.input-t:focus{border-color:#FF3B30}`}</style>
    </div>
  );
}

function EditField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold tracking-[0.2em] uppercase text-white/50 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
